import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { products, productColors } from '@/mocks/products';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import CartDrawer from '@/components/feature/CartDrawer';
import CartFloatingButton from '@/components/feature/CartFloatingButton';

function ProductPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addItem } = useCart();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const product = products.find((p) => p.id === id);

  const [selectedSize, setSelectedSize] = useState(product?.sizes?.[0] || '');
  const [selectedQty, setSelectedQty] = useState(1);
  const [currentImageIdx, setCurrentImageIdx] = useState(0);
  const [addedFlash, setAddedFlash] = useState(false);
  const touchStartX = useRef(0);

  const goToCatalog = () => navigate('/', { state: { scrollTo: 'catalogo' } });
  const goToTop = () => navigate('/', { state: { scrollTo: 'top' } });

  if (!product) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <div className="flex flex-col items-center justify-center py-32 px-4">
          <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4" />
          <h1 className="font-heading text-xl font-semibold text-foreground-900 mb-2">Produto não encontrado</h1>
          <p className="text-sm text-foreground-500 mb-6">O produto que você procura não está disponível.</p>
          <button
            type="button"
            onClick={goToCatalog}
            className="px-6 py-2.5 rounded-full bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors whitespace-nowrap"
          >
            Voltar ao Catálogo
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const imgs = product.images || [product.image];
  const currentImage = imgs[currentImageIdx] || imgs[0];

  const handleAddToCart = () => {
    const size = selectedSize || product.sizes?.[0] || '';
    addItem({
      productId: product.id,
      productName: product.name,
      categoryLabel: product.categoryLabel,
      image: product.image,
      size,
      price: product.price,
      quantity: selectedQty,
    });
    setAddedFlash(true);
    setTimeout(() => setAddedFlash(false), 1500);
  };

  const handleThumbClick = (idx: number) => {
    setCurrentImageIdx(idx);
  };

  const handlePrevImage = () => {
    setCurrentImageIdx((prev) => (prev === 0 ? imgs.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIdx((prev) => (prev === imgs.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <CartDrawer />
      <CartFloatingButton />

      {/* Breadcrumb */}
      <div className="pt-16 md:pt-20 bg-background-50 border-b border-background-200/70">
        <div className="w-full px-4 md:px-6 lg:px-8 max-w-6xl mx-auto py-3">
          <nav className="flex items-center gap-2 text-xs text-foreground-500">
            <button type="button" onClick={goToTop} className="hover:text-primary-600 transition-colors">
              Início
            </button>
            <i className="ri-arrow-right-s-line text-xs" />
            <button type="button" onClick={goToCatalog} className="hover:text-primary-600 transition-colors">
              Catálogo
            </button>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-700">{product.name}</span>
          </nav>
        </div>
      </div>

      {/* Product Detail */}
      <main className="w-full px-4 md:px-6 lg:px-8 max-w-6xl mx-auto py-10 md:py-16">
        <div className="flex flex-col lg:flex-row gap-8 md:gap-12">
          {/* Left: Image Gallery */}
          <div className="w-full lg:w-[55%]">
            {/* Main Image */}
            <div
              className="relative aspect-[3/4] bg-background-100 rounded-xl overflow-hidden mb-4"
              onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
              onTouchEnd={(e) => {
                const diff = touchStartX.current - e.changedTouches[0].clientX;
                if (Math.abs(diff) > 40) {
                  diff > 0 ? handleNextImage() : handlePrevImage();
                }
              }}
            >
              <img
                src={currentImage}
                alt={product.name}
                className="w-full h-full object-cover"
              />

              {/* Nav Arrows */}
              {imgs.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={handlePrevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/90 text-foreground-700 hover:bg-white hover:text-foreground-900 transition-all shadow-sm"
                  >
                    <i className="ri-arrow-left-s-line text-lg" />
                  </button>
                  <button
                    type="button"
                    onClick={handleNextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/90 text-foreground-700 hover:bg-white hover:text-foreground-900 transition-all shadow-sm"
                  >
                    <i className="ri-arrow-right-s-line text-lg" />
                  </button>
                </>
              )}

              {product.isNew && (
                <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-primary-600 text-white text-xs font-medium">
                  Lançamento
                </span>
              )}
            </div>

            {/* Thumbnails */}
            {imgs.length > 1 && (
              <div className="flex items-center gap-3 overflow-x-auto pb-1">
                {imgs.map((img, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handleThumbClick(i)}
                    className={`flex-shrink-0 w-16 h-20 md:w-20 md:h-24 rounded-lg overflow-hidden border-2 transition-all ${
                      i === currentImageIdx
                        ? 'border-primary-500 shadow-sm'
                        : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={img} alt={`${product.name} - foto ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: Product Info */}
          <div className="w-full lg:w-[45%]">
            <span className="inline-block text-xs font-medium text-primary-700 tracking-wider uppercase mb-2">
              {product.categoryLabel}
            </span>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              {product.name}
            </h1>
            <p className="text-sm text-foreground-600 leading-relaxed mb-6">
              {product.description}
            </p>

            {product.price && (
              <div className="mb-6">
                <span className="text-2xl md:text-3xl font-bold text-primary-700">
                  R$ {product.price.toFixed(2)}
                </span>
              </div>
            )}

            {/* Colors */}
            <div className="mb-5">
              <span className="block text-xs font-medium text-foreground-700 mb-2.5">Cores disponíveis:</span>
              <div className="flex items-center gap-2.5">
                {product.colors.map((colorName) => {
                  const color = productColors.find((c) => c.name === colorName);
                  return (
                    <div key={colorName} className="flex items-center gap-1.5">
                      <span
                        className="w-7 h-7 rounded-full border-2 border-background-300"
                        style={{ backgroundColor: color?.value || '#ddd' }}
                        title={colorName}
                      />
                      <span className="text-xs text-foreground-600">{colorName}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Size */}
            <div className="mb-5">
              <span className="block text-xs font-medium text-foreground-700 mb-2.5">Tamanho:</span>
              <div className="flex flex-wrap items-center gap-2">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSelectedSize(s)}
                    className={`w-12 h-10 flex items-center justify-center rounded-lg text-sm font-medium transition-all ${
                      selectedSize === s
                        ? 'bg-primary-600 text-white shadow-sm'
                        : 'bg-background-100 text-foreground-700 border border-background-200 hover:border-primary-300'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div className="mb-6">
              <span className="block text-xs font-medium text-foreground-700 mb-2.5">Quantidade:</span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setSelectedQty((q) => Math.max(1, q - 1))}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors border border-background-200"
                >
                  <i className="ri-subtract-line" />
                </button>
                <span className="w-12 text-center text-lg font-semibold text-foreground-900">{selectedQty}</span>
                <button
                  type="button"
                  onClick={() => setSelectedQty((q) => q + 1)}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors border border-background-200"
                >
                  <i className="ri-add-line" />
                </button>
              </div>
            </div>

            {/* Add to Cart Button */}
            <button
              type="button"
              onClick={handleAddToCart}
              className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-base font-semibold transition-all whitespace-nowrap ${
                addedFlash
                  ? 'bg-accent-500 text-white'
                  : 'bg-primary-600 text-white hover:bg-primary-700 shadow-sm'
              }`}
            >
              <i className={addedFlash ? 'ri-check-line text-lg' : 'ri-shopping-bag-2-line text-lg'} />
              {addedFlash ? 'Adicionado ao Carrinho!' : 'Adicionar ao Carrinho'}
            </button>

            {/* Back to Catalog */}
            <button
              type="button"
              onClick={goToCatalog}
              className="w-full mt-3 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-medium text-foreground-600 bg-background-100 hover:bg-background-200 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              Voltar ao Catálogo
            </button>

            {/* Info badges */}
            <div className="mt-8 flex flex-wrap gap-3">
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background-100 text-xs text-foreground-600">
                <i className="ri-truck-line text-sm text-primary-600" />
                Entrega em todo Brasil
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background-100 text-xs text-foreground-600">
                <i className="ri-shield-check-line text-sm text-primary-600" />
                Qualidade garantida
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background-100 text-xs text-foreground-600">
                <i className="ri-arrow-go-back-line text-sm text-primary-600" />
                Troca facilitada
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default ProductPage;